class PrintService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.printQueue = [];
    this.isProcessing = false;
  }

  // Connect to desktop print server
  connect(serverUrl = 'ws://localhost:8080') {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(serverUrl);
        
        this.ws.onopen = () => {
          console.log('Connected to print server');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = () => {
          console.log('Disconnected from print server');
          this.isConnected = false;
          this.emit('disconnected');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  // Handle incoming messages from desktop
  handleMessage(data) {
    switch (data.type) {
      case 'printers':
        this.emit('printers', data.printers);
        break;
      case 'printStatus':
        this.emit('printStatus', data);
        break;
      case 'queueUpdate':
        this.emit('queueUpdate', data);
        break;
      case 'connectionStatus':
        this.emit('connectionStatus', data);
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  // Send message to desktop
  send(type, data = {}) {
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, ...data }));
      return true;
    }
    return false;
  }

  // Request available printers
  getPrinters() {
    return this.send('getPrinters');
  }

  // Send print job to desktop
  printImage(imageData, options = {}) {
    const printJob = {
      id: Date.now().toString(),
      imageData,
      timestamp: new Date().toISOString(),
      options: {
        printerName: options.printerName || 'default',
        copies: options.copies || 1,
        size: options.size || '4x6',
        quality: options.quality || 'standard',
        ...options
      }
    };

    this.printQueue.push(printJob);
    this.send('print', printJob);
    this.emit('jobQueued', printJob);
    this.processQueue();
    
    return printJob.id;
  }

  // Process print queue
  async processQueue() {
    if (this.isProcessing || this.printQueue.length === 0) return;
    
    this.isProcessing = true;
    
    while (this.printQueue.length > 0) {
      const job = this.printQueue[0];
      this.emit('jobProcessing', job);
      
      // Wait for job completion or timeout
      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          this.emit('jobTimeout', job);
          this.printQueue.shift();
          resolve();
        }, 30000); // 30 second timeout
        
        const onStatus = (data) => {
          if (data.jobId === job.id && data.status === 'completed') {
            clearTimeout(timeout);
            this.printQueue.shift();
            this.emit('jobCompleted', job);
            resolve();
          }
        };
        
        this.on('printStatus', onStatus);
      });
    }
    
    this.isProcessing = false;
  }

  // Get queue status
  getQueueStatus() {
    return {
      queue: this.printQueue,
      isProcessing: this.isProcessing,
      length: this.printQueue.length
    };
  }

  // Cancel print job
  cancelJob(jobId) {
    const index = this.printQueue.findIndex(job => job.id === jobId);
    if (index !== -1) {
      const job = this.printQueue.splice(index, 1)[0];
      this.send('cancelJob', { jobId });
      this.emit('jobCancelled', job);
      return true;
    }
    return false;
  }

  // Clear entire queue
  clearQueue() {
    this.printQueue = [];
    this.send('clearQueue');
    this.emit('queueCleared');
  }

  // Event listeners
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(data));
    }
  }

  // Auto-reconnect logic
  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect().catch(error => {
          console.error('Reconnection failed:', error);
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
      this.emit('reconnectFailed');
    }
  }

  // Disconnect
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  // Check connection status
  isReady() {
    return this.isConnected && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const printService = new PrintService();

export default printService;
